import { useEffect } from 'react';

import { Loader2 } from 'lucide-react';

import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';
import DiscountForm from 'components/DiscountForm';
import OrderReceipt from 'components/OrderReceipt';

import { Trans, t } from '@lingui/macro';
import { useCheckoutContext } from 'context/CheckoutContext';
import { buildReceipt } from 'services/receipt';
import { googleAnalytics } from 'services/googleAnalytics';

export default function OrderSummary() {
    const {
        event,
        discountFormState,
        handleDiscountChange: onDiscountChange,
        handleDiscountSubmit: onDiscountSubmit,
        handleDiscountDelete: onDiscountDelete,
        handleFormSubmit: onClick,
        disablePurchase,
        status,
        errorMessage,
        showDiscountForm,
        products,
        bundles,
        discount,
        seatsPicked,
        robinOptIn,
        setRobinOptIn,
    } = useCheckoutContext();

    const receipt = buildReceipt(products, bundles, event, discount, seatsPicked);

    const { total } = receipt;

    useEffect(() => {
        googleAnalytics.beginCheckout(
            total,
            products.map((product) => ({
                item_id: product.id,
                quantity: product.qty,
                price: product.price,
                item_name: product.name,
            })),
            discountFormState.code,
            event.currency,
        );
        // we only need that on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <h3 className="mt-0 text-foreground">
                <Trans>Order Summary</Trans>
            </h3>

            {showDiscountForm && (
                <DiscountForm
                    code={discountFormState.code}
                    status={discountFormState.status}
                    message={discountFormState.message}
                    onChange={onDiscountChange}
                    onDelete={onDiscountDelete}
                    onSubmit={onDiscountSubmit}
                />
            )}

            <OrderReceipt {...receipt} />

            {event.robinEnabled && (
                <Card className="mb-4 mt-4 rounded-md border border-border p-3">
                    <label className="flex items-start gap-2 text-foreground">
                        <input
                            type="checkbox"
                            checked={robinOptIn}
                            onChange={(e) => setRobinOptIn(e.target.checked)}
                            className="mt-1 h-4 w-4 shrink-0 accent-primary"
                        />
                        <span className="text-sm">
                            <span className="block font-semibold">
                                <Trans>Text me event updates and quick answers via text.</Trans>
                            </span>
                            <span className="mt-1 block text-muted-foreground">
                                <Trans>
                                    By opting in, you agree to receive event-related SMS messages
                                    from Robin, the event's automated support assistant. Msg & data
                                    rates may apply. Reply STOP to unsubscribe anytime.
                                </Trans>
                            </span>
                        </span>
                    </label>
                </Card>
            )}

            <p className="mb-4 mt-4 text-center text-sm text-muted-foreground">
                <Trans>Please note: All purchases are final, and refunds are not available.</Trans>
            </p>

            {errorMessage ? (
                <p className="text-center text-sm text-destructive">{errorMessage}</p>
            ) : null}

            <div className="mb-4 mt-4 flex w-full justify-center">
                <Button
                    type="button"
                    variant="default"
                    onClick={onClick}
                    disabled={disablePurchase}
                    className="h-11 w-[75%] max-w-md font-bold uppercase tracking-wide shadow-none disabled:bg-muted disabled:text-foreground disabled:opacity-100"
                >
                    {status === 'Submit' ? (
                        <Loader2 className="h-6 w-6 shrink-0 animate-spin" aria-hidden />
                    ) : (
                        t`Complete Your Order`
                    )}
                </Button>
            </div>
        </div>
    );
}
